import { createRoot, type Root } from 'react-dom/client';
import { App, type AppProps } from './App';
import {
  AccumulatorPage,
  type AccumulatorMountProps,
} from './features/accumulator/AccumulatorPage';
import { AutoRefillPage, type AutoRefillMountProps } from './features/auto-refill/AutoRefillPage';
import { MyAccountPage, type MyAccountMountProps } from './features/my-account/MyAccountPage';
import { ErrorBoundary } from './components/ErrorBoundary';

type MountTarget = string | HTMLElement;

declare global {
  interface Window {
    MaxorReactRefills?: {
      mount: (target: MountTarget, props: AppProps) => boolean;
      unmount: () => void;
      mountAutoRefill: (target: MountTarget, props: AutoRefillMountProps) => boolean;
      unmountAutoRefill: () => void;
      mountMyAccount: (target: MountTarget, props: MyAccountMountProps) => boolean;
      unmountMyAccount: () => void;
      mountAccumulator: (target: MountTarget, props: AccumulatorMountProps) => boolean;
      unmountAccumulator: () => void;
    };
  }
}

let activeRoot: Root | null = null;
let activeContainer: HTMLElement | null = null;

let activeAutoRefillRoot: Root | null = null;
let activeAutoRefillContainer: HTMLElement | null = null;

let activeMyAccountRoot: Root | null = null;
let activeMyAccountContainer: HTMLElement | null = null;

let activeAccumulatorRoot: Root | null = null;
let activeAccumulatorContainer: HTMLElement | null = null;

function resolveTarget(target: MountTarget): HTMLElement | null {
  if (typeof target === 'string') {
    return document.getElementById(target);
  }

  return target;
}

function unmount(): void {
  if (activeRoot) {
    activeRoot.unmount();
  }

  activeRoot = null;
  activeContainer = null;
}

function mount(target: MountTarget, props: AppProps): boolean {
  const container = resolveTarget(target);
  if (!container) {
    return false;
  }

  if (activeContainer && activeContainer !== container) {
    unmount();
  }

  if (activeRoot && activeContainer === container && process.env.NODE_ENV !== 'production') {
    console.warn(
      '[MaxorReactRefills] mount() called on an already-mounted container without a prior unmount(). ' +
        'This may indicate a missing $destroy lifecycle call in the AngularJS host.'
    );
  }

  if (!activeRoot) {
    activeRoot = createRoot(container);
    activeContainer = container;
  }

  activeRoot.render(
    <ErrorBoundary customerServicePhone={props.config.customerServicePhone}>
      <App {...props} />
    </ErrorBoundary>
  );
  return true;
}

function unmountAutoRefill(): void {
  if (activeAutoRefillRoot) {
    activeAutoRefillRoot.unmount();
  }

  activeAutoRefillRoot = null;
  activeAutoRefillContainer = null;
}

function mountAutoRefill(target: MountTarget, props: AutoRefillMountProps): boolean {
  const container = resolveTarget(target);
  if (!container) {
    return false;
  }

  if (activeAutoRefillContainer && activeAutoRefillContainer !== container) {
    unmountAutoRefill();
  }

  if (
    activeAutoRefillRoot &&
    activeAutoRefillContainer === container &&
    process.env.NODE_ENV !== 'production'
  ) {
    console.warn(
      '[MaxorReactRefills] mountAutoRefill() called on an already-mounted container without a prior unmountAutoRefill(). ' +
        'This may indicate a missing $destroy lifecycle call in the AngularJS host.'
    );
  }

  if (!activeAutoRefillRoot) {
    activeAutoRefillRoot = createRoot(container);
    activeAutoRefillContainer = container;
  }

  activeAutoRefillRoot.render(
    <ErrorBoundary customerServicePhone={props.config.customerServicePhone}>
      <AutoRefillPage {...props} />
    </ErrorBoundary>
  );
  return true;
}

function unmountMyAccount(): void {
  if (activeMyAccountRoot) {
    activeMyAccountRoot.unmount();
  }

  activeMyAccountRoot = null;
  activeMyAccountContainer = null;
}

function mountMyAccount(target: MountTarget, props: MyAccountMountProps): boolean {
  const container = resolveTarget(target);
  if (!container) {
    return false;
  }

  if (activeMyAccountContainer && activeMyAccountContainer !== container) {
    unmountMyAccount();
  }

  if (
    activeMyAccountRoot &&
    activeMyAccountContainer === container &&
    process.env.NODE_ENV !== 'production'
  ) {
    console.warn(
      '[MaxorReactRefills] mountMyAccount() called on an already-mounted container without a prior unmountMyAccount(). ' +
        'This may indicate a missing $destroy lifecycle call in the AngularJS host.'
    );
  }

  if (!activeMyAccountRoot) {
    activeMyAccountRoot = createRoot(container);
    activeMyAccountContainer = container;
  }

  activeMyAccountRoot.render(
    <ErrorBoundary customerServicePhone={props.config.customerServicePhone}>
      <MyAccountPage {...props} />
    </ErrorBoundary>
  );
  return true;
}

function unmountAccumulator(): void {
  if (activeAccumulatorRoot) {
    activeAccumulatorRoot.unmount();
  }

  activeAccumulatorRoot = null;
  activeAccumulatorContainer = null;
}

function mountAccumulator(target: MountTarget, props: AccumulatorMountProps): boolean {
  const container = resolveTarget(target);
  if (!container) {
    return false;
  }

  if (activeAccumulatorContainer && activeAccumulatorContainer !== container) {
    unmountAccumulator();
  }

  if (
    activeAccumulatorRoot &&
    activeAccumulatorContainer === container &&
    process.env.NODE_ENV !== 'production'
  ) {
    console.warn(
      '[MaxorReactRefills] mountAccumulator() called on an already-mounted container without a prior unmountAccumulator(). ' +
        'This may indicate a missing $destroy lifecycle call in the AngularJS host.'
    );
  }

  if (!activeAccumulatorRoot) {
    activeAccumulatorRoot = createRoot(container);
    activeAccumulatorContainer = container;
  }

  activeAccumulatorRoot.render(
    <ErrorBoundary customerServicePhone={props.config.customerServicePhone}>
      <AccumulatorPage {...props} />
    </ErrorBoundary>
  );
  return true;
}

window.MaxorReactRefills = {
  mount,
  unmount,
  mountAutoRefill,
  unmountAutoRefill,
  mountMyAccount,
  unmountMyAccount,
  mountAccumulator,
  unmountAccumulator,
};
